import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router";

import { useOrganization } from "@/auth/organization";
import { useUser } from "@/auth/session";
import { ChecklistCard } from "@/components/onboarding";
import { cx, Icon, type IconName } from "@/components/ui";
import { SECTION_PERMISSIONS } from "@/lib/access";
import { session } from "@/lib/api";
import { useEffectiveBranding } from "@/lib/branding";
import { initials } from "@/lib/format";

import { BrandMark } from "./Logo";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

/** Items the signed-in person may see: those whose area they hold a permission for. */
export function visibleNav(
  items: readonly NavItem[],
  canAny: (permission: string) => boolean,
  loading: boolean,
): NavItem[] {
  return items.filter((item) => {
    const permission = SECTION_PERMISSIONS[item.to];
    // Until permissions arrive, show only what everyone has (nothing appears that then vanishes).
    return !permission || (!loading && canAny(permission));
  });
}

// MVP: only areas the platform serves today, plus Growth Intelligence (a placeholder page until the
// service lands). Email is the DNS status of professional email for now; mailboxes and aliases are on its Mailboxes page.
const MAIN_NAV: NavItem[] = [
  { to: "/", label: "Home", icon: "home", end: true },
  { to: "/growth-intelligence", label: "Growth Intelligence", icon: "trend-up" },
  { to: "/websites", label: "Website", icon: "website" },
  { to: "/domains", label: "Domains", icon: "domains" },
  { to: "/email", label: "Email", icon: "mail" },
  { to: "/hosting", label: "Hosting", icon: "server" },
  { to: "/analytics", label: "Analytics", icon: "analytics" },
  { to: "/notifications", label: "Notifications", icon: "notifications" },
];

const MANAGE_NAV: NavItem[] = [
  { to: "/billing", label: "Billing", icon: "billing" },
  { to: "/team", label: "Team & roles", icon: "users" },
  { to: "/audit", label: "Audit log", icon: "shield-tick" },
  { to: "/activity", label: "Activity", icon: "activity-heart" },
  { to: "/policies", label: "Policies", icon: "scales" },
  { to: "/support", label: "Support", icon: "info-circle" },
  { to: "/settings", label: "Settings", icon: "settings" },
];

export function Sidebar({
  collapsed = false,
  onToggleCollapsed,
  onNavigate,
}: {
  collapsed?: boolean;
  /** Omit to hide the collapse control (the mobile drawer). */
  onToggleCollapsed?: () => void;
  onNavigate?: () => void;
}) {
  const [manageOpen, setManageOpen] = useState(true);
  const { orgId, canAny, permissionsLoading } = useOrganization();
  const branding = useEffectiveBranding(orgId);
  const main = visibleNav(MAIN_NAV, canAny, permissionsLoading);
  const manage = visibleNav(MANAGE_NAV, canAny, permissionsLoading);
  return (
    <div
      className={cx(
        "flex h-full flex-col gap-1 overflow-y-auto pt-6 pb-[18px] font-sans",
        collapsed ? "w-[72px] items-center px-3" : "w-[264px] px-3.5",
      )}
    >
      <div className={cx("flex w-full items-center gap-2.5 py-1.5", collapsed && "flex-col")}>
        <BrandMark className="size-7 rounded-lg" logoUrl={branding.logoUrl} colourHex={branding.colourHex} />
        {!collapsed && (
          <span className="text-lg leading-7 font-semibold whitespace-nowrap text-chrome-fg">CortexGrip</span>
        )}
        {!collapsed && <span className="flex-1" />}
        {onToggleCollapsed && (
          <button
            onClick={onToggleCollapsed}
            aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="rounded p-0.5 text-chrome-fg-subtle transition-colors hover:text-chrome-fg"
          >
            <Icon name="chevron-left" size={16} className={cx(collapsed && "rotate-180")} />
          </button>
        )}
      </div>

      <nav className="flex w-full flex-col gap-px pt-2" aria-label="Main" data-tour="main-nav">
        {main.map((item) => (
          <SideLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
      </nav>

      {collapsed ? (
        <div className="my-2 h-px w-8 bg-chrome-raised" />
      ) : (
        <button
          onClick={() => setManageOpen((v) => !v)}
          aria-expanded={manageOpen}
          className="flex w-full items-center justify-between pt-3.5 pb-1.5 pl-2.5 text-xs leading-4 font-semibold text-chrome-fg-subtle"
        >
          MANAGE
          <Icon
            name="manage-chevron"
            size={15}
            className={cx("text-chrome-fg-subtle transition-transform", !manageOpen && "-rotate-90")}
          />
        </button>
      )}
      {(manageOpen || collapsed) && (
        <nav className="flex w-full flex-col gap-1" aria-label="Manage">
          {manage.map((item) => (
            <SideLink key={item.to} item={item} collapsed={collapsed} onNavigate={onNavigate} />
          ))}
        </nav>
      )}

      <div className="min-h-4 flex-1" />
      {!collapsed && <ChecklistCard onNavigate={onNavigate} />}
      <Profile collapsed={collapsed} onNavigate={onNavigate} />
    </div>
  );
}

function SideLink({
  item,
  collapsed,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cx(
          "flex items-center gap-[11px] rounded-[10px] py-[9px] text-sm leading-5 whitespace-nowrap transition-colors",
          collapsed ? "justify-center px-[9px]" : "w-full px-2.5",
          isActive
            ? "bg-brand-500 font-semibold text-chrome-fg"
            : "font-medium text-chrome-fg-muted hover:bg-chrome-raised hover:text-chrome-fg",
        )
      }
    >
      <Icon name={item.icon} />
      {!collapsed && item.label}
    </NavLink>
  );
}

function Profile({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const user = useUser();
  const { current, can } = useOrganization();
  const [menuOpen, setMenuOpen] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const signOut = useMutation({
    mutationFn: () => session.signOut(),
    onSettled: () => {
      qc.clear();
      navigate("/login", { replace: true });
    },
  });
  const name = user.display_name || user.email;
  const role = current.roles[0]?.name ?? "Member";

  return (
    <div
      className={cx("relative flex w-full items-center gap-2.5 px-0.5 py-1.5", collapsed && "justify-center")}
    >
      <Avatar user={user} className="size-8 rounded-lg" />
      {!collapsed && (
        <span className="flex min-w-0 flex-1 flex-col gap-px">
          <span className="truncate text-sm leading-5 font-semibold text-chrome-fg">{shortName(name)}</span>
          <span className="truncate text-xs leading-4 text-chrome-fg-subtle">
            {role}
            {can("*") && " · full access"}
          </span>
        </span>
      )}
      <button
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="Account menu"
        aria-expanded={menuOpen}
        className={cx(
          "rounded p-0.5 text-chrome-fg-subtle transition-colors hover:text-chrome-fg",
          collapsed && "absolute inset-0 opacity-0",
        )}
      >
        <Icon name="dots" size={17} />
      </button>
      {menuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
          <div
            role="menu"
            className={cx(
              "absolute bottom-full z-50 mb-2 w-56 overflow-hidden rounded-xl border border-border bg-surface py-1 text-sm shadow-lg",
              collapsed ? "left-0" : "right-0",
            )}
          >
            <p className="truncate border-b border-border px-3 py-2 text-xs text-fg-subtle">{user.email}</p>
            <Link
              role="menuitem"
              to="/settings"
              onClick={() => {
                setMenuOpen(false);
                onNavigate?.();
              }}
              className="block px-3 py-2 text-fg hover:bg-muted"
            >
              Account settings
            </Link>
            <button
              role="menuitem"
              onClick={() => signOut.mutate()}
              className="block w-full px-3 py-2 text-left text-fg hover:bg-muted"
            >
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/** "Riley Jordan" → "Riley J." as in the design; e-mail addresses are left alone. */
function shortName(name: string): string {
  const parts = name.trim().split(/\s+/);
  return parts.length > 1 ? `${parts[0]} ${parts.at(-1)![0]}.` : name;
}

export function Avatar({
  user,
  className,
}: {
  user: { display_name: string; email: string; avatar_url: string | null };
  className?: string;
}) {
  return user.avatar_url ? (
    <img src={user.avatar_url} alt="" className={cx("shrink-0 object-cover", className)} />
  ) : (
    <span
      className={cx(
        "flex shrink-0 items-center justify-center bg-brand-100 text-xs font-semibold text-[#5c5551]",
        className,
      )}
      aria-hidden
    >
      {initials(user.display_name || user.email)}
    </span>
  );
}
